import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'

import useCampaignDetails from './useCampaignDetails'

import { transformValuesToAssetsCreation, transformValuesToBE } from '../formatters'

import {
  clearCreateAssetGroup,
  clearCreateAssetsBatch,
  createAssetGroup,
  createAssetsBatch
} from '../../../../../../../../modules/actions/assets'
import useDoubleRequestSubmit from '../../../../../../../../hooks/formHooks/useDoubleRequestSubmit'
import {
  assetGroupCreateErrorSelector,
  assetGroupWasCreatedSelector,
  assetsCreatedDataSelector,
  assetsCreateErrorSelector,
  assetsWasCreatedSelector
} from '../../../../../../../../modules/selectors/assets'
import { selectedGoogleAdAccountSelector } from '../../../../../../../../modules/selectors/adAccounts'
import { selectedPartnershipRightsHolderIdSelector } from '../../../../../../../../modules/selectors/partnerships'

import { STATUS } from '../../../../../fields'
import { IS_QUARANTINE } from '../../../../../../../reusableFields'

// GoogleAssetGroupMainForm form submission consists of 2 different requests:
// createAssetsBatch and then createAssetGroup, once first successfully submitted the 2nd is triggerred
export default function useGoogleAssetGroupCreate({ isInternalAssetGroupCreation }) {
  const dispatch = useDispatch()

  const [submittedAssetsList, setSubmittedAssetsList] = useState([])

  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const rightsHolderId = useSelector(selectedPartnershipRightsHolderIdSelector)

  const { campaignId } = useCampaignDetails()

  const createAssetsHandler = useCallback(
    values => {
      const assetsData = transformValuesToAssetsCreation(values, selectedAdAccountId)
      // assetsData which is transformed to BE need to be saved to state as it is also used for 2nd request createAssetGroup
      setSubmittedAssetsList(assetsData)

      dispatch(
        createAssetsBatch({
          account: selectedAdAccountId,
          operations: assetsData
        })
      )
    },
    [dispatch, selectedAdAccountId]
  )

  const createAssetGroupHandler = useCallback(
    (values, createdAssetsData) => {
      const transformedValues = transformValuesToBE(values, createdAssetsData, submittedAssetsList)

      dispatch(
        createAssetGroup({
          ...transformedValues,
          account: selectedAdAccountId,
          campaign: campaignId,
          status: values[STATUS],
          // isInternalAssetGroupCreation is default, also allow to pass it manually if user has ability to select it(values[IS_QUARANTINE])
          ...((isInternalAssetGroupCreation || values[IS_QUARANTINE]) && {
            quarantine: true,
            account_id: rightsHolderId,
            rights_holder_account_id: rightsHolderId
          })
        })
      )
    },
    [campaignId, dispatch, isInternalAssetGroupCreation, rightsHolderId, selectedAdAccountId, submittedAssetsList]
  )

  const clearSubmitHandler = useCallback(() => {
    dispatch(clearCreateAssetsBatch())
    dispatch(clearCreateAssetGroup())
  }, [dispatch])

  const createAssetRequest = useMemo(
    () => ({
      requestHandler: createAssetsHandler,
      errorSelector: assetsCreateErrorSelector,
      successSelector: assetsWasCreatedSelector,
      dataSelector: assetsCreatedDataSelector
    }),
    [createAssetsHandler]
  )

  const createAssetGroupRequest = useMemo(
    () => ({
      requestHandler: createAssetGroupHandler,
      errorSelector: assetGroupCreateErrorSelector,
      successSelector: assetGroupWasCreatedSelector
    }),
    [createAssetGroupHandler]
  )

  const requests = useMemo(
    () => [createAssetRequest, createAssetGroupRequest],
    [createAssetRequest, createAssetGroupRequest]
  )

  return useDoubleRequestSubmit({ requests, clearSubmitHandler })
}
